import React from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  BarChart3, 
  Target, 
  Lightbulb, 
  Heart, 
  Zap, 
  Brain,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowUp,
  ArrowDown,
  Minus,
  RefreshCw,
  Sparkles,
  Star,
  Flower
} from 'lucide-react';

interface GeneratedInsight {
  id: string;
  type: 'cycle_prediction' | 'pattern_analysis' | 'symptom_correlation' | 'health_tip' | 'mood_pattern' | 'flow_analysis' | 'lifestyle_correlation' | 'prediction_accuracy';
  title: string;
  description: string;
  data: any;
  priority: 'high' | 'medium' | 'low';
  actionable: boolean;
  recommendation?: string;
  confidence: number;
  trend?: 'improving' | 'stable' | 'concerning';
  visualData?: any;
}

interface InsightCardProps {
  insight: GeneratedInsight;
  index: number;
}

const InsightCard: React.FC<InsightCardProps> = ({ insight, index }) => {
  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'cycle_prediction': return TrendingUp;
      case 'pattern_analysis': return BarChart3;
      case 'symptom_correlation': return Target;
      case 'health_tip': return Lightbulb;
      case 'mood_pattern': return Heart;
      case 'flow_analysis': return Zap;
      case 'prediction_accuracy': return CheckCircle;
      default: return Brain;
    }
  };

  const getInsightColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'from-rose-400 to-pink-500';
      case 'medium': return 'from-amber-400 to-orange-500';
      case 'low': return 'from-emerald-400 to-teal-500';
      default: return 'from-purple-400 to-indigo-500';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return AlertCircle;
      case 'medium': return Clock;
      case 'low': return CheckCircle;
      default: return Brain;
    }
  };

  const getTrendIcon = (trend?: string) => {
    switch (trend) {
      case 'improving': return ArrowUp;
      case 'concerning': return ArrowDown;
      case 'stable': return Minus;
      default: return RefreshCw;
    }
  };

  const getTrendColor = (trend?: string) => {
    switch (trend) {
      case 'improving': return 'text-emerald-600';
      case 'concerning': return 'text-rose-600';
      case 'stable': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const IconComponent = getInsightIcon(insight.type);
  const PriorityIcon = getPriorityIcon(insight.priority);
  const TrendIcon = getTrendIcon(insight.trend);

  return (
    <motion.div
      className="bg-gradient-to-br from-white via-pink-50/30 to-purple-50/30 rounded-2xl border border-pink-100 p-6 hover:shadow-lg transition-all duration-300 relative overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -2, scale: 1.01 }}
    >
      {/* Decorative Elements */}
      <div className="absolute top-2 right-2 w-16 h-16 bg-gradient-to-br from-pink-200/20 to-purple-200/20 rounded-full blur-xl"></div>
      <div className="absolute bottom-2 left-2 w-12 h-12 bg-gradient-to-br from-rose-200/20 to-pink-200/20 rounded-full blur-lg"></div>
      
      <div className="flex items-start space-x-4 relative z-10">
        <motion.div
          className={`p-3 bg-gradient-to-br ${getInsightColor(insight.priority)} rounded-xl shadow-lg`}
          whileHover={{ scale: 1.1, rotate: 5 }}
        >
          <IconComponent className="w-6 h-6 text-white" />
        </motion.div>
        
        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 text-lg">{insight.title}</h4>
            <div className="flex items-center space-x-2">
              {/* Confidence Badge */}
              <motion.span 
                className="px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 text-xs rounded-full font-medium border border-blue-200"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                <Sparkles className="w-3 h-3 inline mr-1" />
                {insight.confidence}% confidence
              </motion.span>
              
              {/* Priority Badge */}
              <motion.div
                className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${
                  insight.priority === 'high' ? 'bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 border border-rose-200' :
                  insight.priority === 'medium' ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 border border-amber-200' :
                  'bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 border border-emerald-200'
                }`}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                <PriorityIcon className="w-3 h-3" />
                <span>{insight.priority}</span>
              </motion.div>

              {/* Trend Indicator */}
              {insight.trend && (
                <motion.div
                  className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-gray-100 to-gray-200 ${getTrendColor(insight.trend)} border border-gray-200`}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <TrendIcon className="w-3 h-3" />
                  <span>{insight.trend}</span>
                </motion.div>
              )}
            </div>
          </div>
          
          <p className="text-gray-700 mb-4 leading-relaxed">{insight.description}</p>
          
          {/* Data Visualization */}
          {insight.visualData && (
            <motion.div
              className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              {insight.visualData.chartType === 'progress' && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span className="font-medium">{insight.visualData.data.label}</span>
                    <span className="font-bold text-purple-600">{insight.visualData.data.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <motion.div
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full shadow-sm"
                      initial={{ width: 0 }}
                      animate={{ width: `${insight.visualData.data.percentage}%` }}
                      transition={{ delay: 0.5 + index * 0.1, duration: 1 }}
                    />
                  </div>
                </div>
              )}

              {insight.visualData.chartType === 'gauge' && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {insight.visualData.data.value}%
                  </div>
                  <div className="text-sm text-gray-600">{insight.visualData.data.label}</div>
                </div>
              )}

              {insight.visualData.chartType === 'bar' && insight.visualData.data && (
                <div className="space-y-3">
                  {insight.visualData.data.slice(0, 3).map((item: any, i: number) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 capitalize font-medium">{item.symptom || item.mood}</span>
                      <div className="flex items-center space-x-3">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <motion.div
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${item.percentage}%` }}
                            transition={{ delay: 0.6 + i * 0.1, duration: 0.8 }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 font-medium w-10 text-right">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
          
          {insight.recommendation && (
            <motion.div
              className="bg-gradient-to-r from-purple-50 via-pink-50 to-rose-50 border border-purple-200 rounded-xl p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <div className="flex items-start space-x-3">
                <motion.div
                  className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex-shrink-0"
                  whileHover={{ scale: 1.1, rotate: 10 }}
                >
                  <Lightbulb className="w-4 h-4 text-white" />
                </motion.div>
                <div>
                  <p className="text-sm font-medium text-purple-900 mb-1 flex items-center">
                    <Star className="w-4 h-4 mr-1 text-purple-600" />
                    Personalized Recommendation
                  </p>
                  <p className="text-sm text-purple-700 leading-relaxed">{insight.recommendation}</p>
                </div>
              </div>
            </motion.div>
          )}
          
          {insight.actionable && (
            <motion.div
              className="mt-4 flex items-center justify-between"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Category:</span>
                <span className="text-xs font-medium text-purple-600 capitalize bg-purple-100 px-2 py-1 rounded-full">
                  {insight.type.replace('_', ' ')}
                </span>
              </div>
              <motion.div
                className="flex items-center space-x-1 text-emerald-600"
                whileHover={{ scale: 1.05 }}
              >
                <Flower className="w-3 h-3" />
                <span className="text-xs font-medium">Actionable</span>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default InsightCard;